import React, { useState, type KeyboardEvent, type ChangeEvent } from "react";
import { Box, Chip, TextField } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";

interface ChipSearchInputProps {
  label: string;
  values: string[];
  options?: string[];
  onChange: (newValues: string[]) => void;
  onBlur?: () => void;
  required?: boolean;
  error?: string;
}

const ChipSearchInput: React.FC<ChipSearchInputProps> = ({
  label,
  values,
  // options = [],
  onChange,
  required = false,
  error,
  onBlur,
}) => {
  const [input, setInput] = useState("");

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && input.trim()) {
      e.preventDefault();
      const value = input.trim();

      if (!values.includes(value)) {
        onChange([...values, value]);
      }
      setInput("");
    }
  };

  const handleDelete = (chipToDelete: string) => {
    onChange(values.filter((val) => val !== chipToDelete));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  return (
    <Box className="space-y-1 w-full">
      <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <TextField
        fullWidth
        value={input}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        placeholder="Type and press Enter"
        variant="outlined"
        error={!!error}
        helperText={error}
        InputProps={{
          sx: {
            backgroundColor: "var(--color-surface)",
            borderRadius: "9999px",
            color: "var(--color-text)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--color-border)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--color-stat-accent)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--color-stat-accent) !important",
            },
          },
        }}
      />

      {values.length > 0 && (
        <Box className="mt-2 flex flex-wrap gap-2">
          {values.map((val) => (
            <Chip
              key={val}
              label={val}
              onDelete={() => handleDelete(val)}
              deleteIcon={<CancelIcon sx={{ color: "var(--color-text) !important", opacity: 0.7, "&:hover": { opacity: 1 } }} />}
              sx={{
                color: "var(--color-text)",
                borderColor: "var(--color-border)",
                backgroundColor: "color-mix(in srgb, var(--color-stat-accent) 10%, transparent)",
                "& .MuiChip-label": {
                  fontSize: "0.85rem",
                  fontWeight: 500,
                },
              }}
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ChipSearchInput;
